import { IconButton, InputBase, Paper, Table, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { styled } from "@mui/material/styles";

const presentItensCounterToolTipTitle = "Produto confere";
const notFoundItensCounterToolTipTitle = "Produto inesperado";
const divergentItensTotalCounterToolTipTitle = "Total não confere";

interface RowUIState {
  backgroundColor: string;
  tooltipTitle?: string;
  iconColor?: string;
  iconHoverColor?: string;
}

export function getRowUIState({
  hasEmptyData,
  hasRemovedItem,
  hasSecondaryData,
  isItemPresent,
  isItemTotalEqual,
  isGettingCounter,
}: {
  hasEmptyData: boolean;
  hasRemovedItem: boolean;
  hasSecondaryData: boolean;
  isItemPresent: boolean;
  isItemTotalEqual: boolean;
  isGettingCounter: boolean;
}): RowUIState {
  if (hasEmptyData) {
    return { backgroundColor: "var(--color-background-surface)" };
  }

  if (hasRemovedItem && !hasSecondaryData) {
    return { backgroundColor: "var(--color-status-not-counted)" };
  }

  if (isItemPresent) {
    if (isItemTotalEqual) {
      return {
        backgroundColor: "var(--color-status-match)",
        tooltipTitle: presentItensCounterToolTipTitle,
        iconColor: "var(--color-status-match-dark)",
        iconHoverColor: "var(--color-status-match-light)",
      };
    }

    return {
      backgroundColor: "var(--color-status-mismatch)",
      tooltipTitle: divergentItensTotalCounterToolTipTitle,
      iconColor: "var(--color-status-mismatch-dark)",
      iconHoverColor: "var(--color-status-mismatch-light)",
    };
  }

  if (!isGettingCounter && hasSecondaryData) {
    return {
      backgroundColor: "var(--color-status-unexpected-light)",
      tooltipTitle: notFoundItensCounterToolTipTitle,
      iconColor: "var(--color-status-unexpected-dark)",
      iconHoverColor: "var(--color-status-unexpected)",
    };
  }

  return {
    backgroundColor: "var(--color-status-not-counted)",
  };
}

export const Container = styled(Paper)(() => ({
  flex: 1,
  borderRadius: `var(--radius-md)`,
  height: "calc(100dvh - 22.5rem)",
  display: "flex",
  flexDirection: "column",
}));

export const CustomTableContainer = styled(TableContainer)(() => ({
  flex: 1,
  overflow: "auto",
}));

export const CustomTable = styled(Table)(() => ({
  maxHeight: "25rem",
  width: "100%",
  backgroundColor: "var(--cardBackgroundColor)",
  borderRadius: "var(--radius-md)",
}));

export const CustomTableHead = styled(TableHead)(() => ({
  position: "sticky",
  top: 0,
  zIndex: 1,
}));

export const HeaderTitleRow = styled(TableRow)(() => ({
  zIndex: 2,
}));

export const HeaderTitleCell = styled(TableCell)(() => ({
  backgroundColor: "var(--color-primary)",
  color: "var(--color-text-inverse)",
  borderBottom: 0,
  fontWeight: "var(--font-weight-bold)",
  letterSpacing: "0.05rem",
  borderTopLeftRadius: "var(--radius-md)",
  borderTopRightRadius: "var(--radius-md)",
}));

export const HeaderTitleContent = styled("span")({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  fontWeight: "var(--font-weight-bold)",
  color: "var(--color-text-inverse)",
  fontSize: "var(--font-size-2xl)",
});

export const HeaderTitleContentIcon = styled("span")({
  position: "absolute",
  right: 0,
});

export const HeaderColumnsRow = styled(TableRow)(() => ({
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

export const CustomHeaderCell = styled(TableCell)({
  backgroundColor: "var(--color-secondary)",
  padding: "var(--space-xl)",
  color: "var(--color-text-inverse)",
  fontWeight: "bolder",
  fontSize: "var(--font-size-clamp)",
});

export const ScannerCell = styled(TableCell)(() => ({
  padding: "var(--space-lg)",
  borderBottom: "none",
}));

export const ScannerCellContent = styled("div")(() => ({
  display: "flex",
  gap: "var(--space-lg)",
  flexWrap: "nowrap",
}));

interface CustomBodyRowProps {
  uiState: RowUIState;
  blink?: boolean;
}

export const CustomBodyRow = styled(TableRow, {
  shouldForwardProp: (prop) => prop !== "uiState" && prop !== "blink",
})<CustomBodyRowProps>(({ uiState, blink }) => ({
  backgroundColor: uiState.backgroundColor,

  ...(blink && {
    animation: "blinkAnimation 0.6s ease-in-out",
  }),

  "@keyframes blinkAnimation": {
    "0%, 100%": { opacity: 1 },
    "50%": { opacity: 0.1 },
  },
}));

export const CustomBodyCell = styled(TableCell)(() => ({
  color: "var(--color-text-primary)",
  fontSize: "var(--font-size-clamp)",
}));

export const CustomEditableTableCell = styled(TableCell)(() => ({
  padding: "0 var(--space-xxs)",
  color: "var(--color-text-primary)",
  fontSize: "var(--font-size-clamp)",
  lineHeight: "1.4em",
  height: "1.4em",
}));

export const CustomEditableCellInput = styled(InputBase)(() => ({
  fontSize: "inherit",
  width: "auto",
  maxWidth: "12rem",

  "& input": {
    padding: "0 var(--space-xxs)",
    margin: 0,
    borderRadius: "var(--radius-xxs)",
    backgroundColor: "var(--color-background-field-hover)",
    boxShadow: "var(--shadow-sm)",
    outline: "none",
    minWidth: 0,
    width: "100%",
  },
}));

export const CustomEditableCellDisplayValueContainer = styled("span")(() => ({
  display: "inline-block",

  minHeight: "1.4em",
  lineHeight: "1.4em",
  padding: "0 var(--space-xxs)",
}));

export const CustomEditableCellDisplayValueContent = styled("div")(() => ({
  display: "flex",
  gap: "var(--space-xs)",
}));

export const CenteredCellContent = styled("span")({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

interface StatusIconContainerProps {
  align?: "left" | "center" | "right";
}

export const StatusIconContainer = styled("span")<StatusIconContainerProps>(({ align = "left" }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: align,
}));

export const ActionsContainer = styled(Paper)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "left",
  backgroundColor: "var(--color-background-surface)",
  borderRadius: 0,
  borderTopLeftRadius: "var(--radius-lg)",
  borderBottomLeftRadius: "var(--radius-lg)",
}));

interface ActionIconButtonProps {
  hoverColor?: string;
}

const DEFAULT_ACTION_ICON_BUTTON_HOVER_COLOR = "var(--color-primary)";
const DEFAULT_ACTION_ICON_BUTTON_COLOR = "var(--color-text-primary)";

export const ActionIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== "hoverColor",
})<ActionIconButtonProps>(({ hoverColor = DEFAULT_ACTION_ICON_BUTTON_HOVER_COLOR }) => ({
  color: DEFAULT_ACTION_ICON_BUTTON_COLOR,
  cursor: "pointer",
  transition: "color 0.2s ease-in-out",
  "&:hover": {
    color: hoverColor,
  },
}));

export const CustomTableFooter = styled(Paper)(() => ({
  padding: "var(--space-lg)",
  display: "flex",
  justifyContent: "flex-end",
  backgroundColor: "var(--color-background-surface)",
  borderBottomLeftRadius: "var(--radius-md)",
  borderBottomRightRadius: "var(--radius-md)",
}));

export const TableFooterContainer = styled("span")(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  margin: "var(--space-sm)",
  gap: "var(--space-xs)",
}));

export const TableFooterTitle = styled("span")(() => ({
  fontSize: "var(--font-size-xl)",
  fontWeight: "var(--font-weight-bold)",
  color: "var(--color-text-primary)",
}));

export const TableFooterNumber = styled("span")(() => ({
  fontSize: "var(--font-size-base)",
  fontWeight: "var(--font-weight-regular)",
  color: "var(--color-text-secondary)",
}));
